import React, { useState, useEffect, useCallback } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db, auth } from '../../config/firebase';
import { motion } from 'framer-motion';
import { UpdateRankingsButton } from '../UpdateRankingsButton';
import { SimulationControls } from '../SimulationControls';
import { AdminMetrics } from '../AdminMetrics';
import { AdminDebugPanel } from '../AdminDebugPanel';
import './styles.css';

// All available songs
const ALL_SONGS = [
  "WHERE HAVE I BEEN? - MONOLOGUE",
  "MYSTERY SONG COVER",
  "H3@RT$ W3RE M3@NT T0 F7Y",
  "SURVIVE",
  "CRUISIN",
  "WORST GUYS",
  "TALK MY SHIT",
  "GOT 2 BE",
  "IN THE NIGHT",
  "YOSHINOYA",
  "TO BE HUNTED",
  "URN (FULL VERSION)",
  "CRAWL",
  "WE ARE GOD",
  "FEELS LIKE SUMMER",
  "HUMAN SACRIFICE",
  "PLACE WHERE LOVE GOES",
  "NO EXCUSES",
  "ME AND UR MAMA",
  "DO YA LIKE",
  "SOBER",
  "LES",
  "HEARTBEAT",
  "SWEATPANTS",
  "THIS IS AMERICA",
  "BONFIRE",
  "3005",
  "REDBONE",
  "LITHONIA",
  "REAL LOVE",
  "DAMN RIGHT (JENNIE)",
  "WITCHY (KAYTRANADA)",
  "URN",
  "TELEGRAPH AVE",
  "SUMMERTIME MAGIC",
  "THE PALISADES",
  "IV. SWEATPANTS",
  "BLACK QUALLS (THUNDERCAT)",
  "RETRO (ROUGH)",
  "I. PINK TOES",
  "TERRIFIED",
  "FAVORITE SONG (CHANCE)",
  "BABY BOY",
  "WHAT KIND OF LOVE",
  "DADVOCATE",
  "FREAKS AND GEEKS",
  "STEPS BEACH",
  "PSILOCYBAE",
  "RIOT",
  "LITTLE FOOT BIG FOOT",
];

/**
 * Admin Setlist Controller
 * 
 * Allows admins to manually control the live setlist rankings using sliders.
 * Changes are saved to Firebase and propagate to all users in real-time.
 */
export const AdminSetlistController = () => {
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  
  // Check if current user is admin
  useEffect(() => {
    const checkAdminStatus = async () => {
      const user = auth.currentUser;
      if (!user) {
        setIsAdmin(false);
        setLoading(false);
        return;
      }
      
      try {
        const adminDoc = await getDoc(doc(db, 'adminUsers', user.uid));
        const isUserAdmin = adminDoc.exists() && adminDoc.data().role === 'admin';
        setIsAdmin(isUserAdmin);
        
        if (isUserAdmin) {
          loadSetlist();
        } else {
          setLoading(false);
        }
      } catch (err) {
        console.error('Error checking admin status:', err);
        setIsAdmin(false);
        setLoading(false);
      }
    };
    
    checkAdminStatus();
  }, []);
  
  // Load current setlist from Firebase
  const loadSetlist = async () => {
    try {
      setLoading(true);
      const docRef = doc(db, 'adminSetlist', 'currentSetlist');
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        setSongs(data.songs || []);
      } else {
        // Initialize with default songs
        const defaultSongs = ALL_SONGS.map((title, index) => ({
          title,
          score: 1 - (index / ALL_SONGS.length), // Descending scores
          rank: index + 1
        }));
        setSongs(defaultSongs);
      }
      setLoading(false);
    } catch (err) {
      console.error('Error loading setlist:', err);
      setError('Failed to load setlist. Please try again.');
      setLoading(false);
    }
  };
  
  // Update song score
  const updateSongScore = useCallback((title, newScore) => {
    setSongs(prevSongs => {
      const updated = prevSongs.map(song =>
        song.title === title ? { ...song, score: newScore } : song
      );
      
      // Sort by score and update ranks
      const sorted = [...updated].sort((a, b) => b.score - a.score);
      const withRanks = sorted.map((song, index) => ({
        ...song,
        rank: index + 1
      }));
      
      setHasChanges(true);
      return withRanks;
    });
  }, []);
  
  // Save to Firebase
  const saveToFirebase = async () => {
    const user = auth.currentUser;
    if (!user) {
      setError('You must be logged in to save changes.');
      return;
    }
    
    try {
      setSaving(true);
      setError(null);
      
      const docRef = doc(db, 'adminSetlist', 'currentSetlist');
      await setDoc(docRef, {
        songs: songs.map(({ title, score, rank }) => ({ title, score, rank })),
        updatedAt: new Date().toISOString(),
        updatedBy: user.email,
        lastAdminUpdate: new Date().toISOString(), // Track admin updates
        isManualOverride: true, // Flag to pause simulation
        isActive: true
      });
      
      setHasChanges(false);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
      setSaving(false);
    } catch (err) {
      console.error('Error saving setlist:', err);
      setError('Failed to save changes. Please try again.');
      setSaving(false);
    }
  };
  
  // Reset to defaults
  const resetToDefaults = () => {
    if (window.confirm('Reset all songs to default rankings?')) {
      const defaultSongs = ALL_SONGS.map((title, index) => ({
        title,
        score: 1 - (index / ALL_SONGS.length),
        rank: index + 1
      }));
      setSongs(defaultSongs);
      setHasChanges(true);
    }
  };
  
  // Sorted songs for display
  const sortedSongs = [...songs].sort((a, b) => b.score - a.score);
  
  if (loading) {
    return (
      <div className="admin-controller">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading admin panel...</p>
        </div>
      </div>
    );
  }
  
  if (!isAdmin) {
    return (
      <div className="admin-controller">
        <div className="access-denied">
          <h2>Access Denied</h2>
          <p>You need admin privileges to access this page.</p>
          <p>Please contact the administrator if you believe this is an error.</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="admin-controller">
      {/* Admin-only panels: metrics and debug */}
      <div className="admin-side-panels" style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '1.5rem' }}>
        <AdminMetrics />
        <AdminDebugPanel />
      </div>
      {/* Quick Actions Section */}
      <div style={{ padding: '1.5rem', backgroundColor: 'rgba(254, 218, 45, 0.1)', borderRadius: '8px', marginBottom: '2rem' }}>
        <h3 style={{ color: '#feda2d', marginBottom: '1rem', fontSize: '1.2rem' }}>Quick Actions</h3>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <UpdateRankingsButton />
          <button 
            onClick={resetToDefaults}
            className="btn-secondary"
            disabled={saving}
            style={{
              padding: '0.75rem 1.5rem',
              fontSize: '1rem',
              fontWeight: 'bold',
              backgroundColor: '#333',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.6 : 1
            }}
          >
            Reset to Defaults
          </button>
          <button 
            onClick={saveToFirebase}
            className={`btn-primary ${hasChanges ? 'has-changes' : ''}`}
            disabled={!hasChanges || saving}
            style={{
              padding: '0.75rem 1.5rem',
              fontSize: '1rem',
              fontWeight: 'bold',
              backgroundColor: hasChanges ? '#0FA52A' : '#666',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              cursor: (!hasChanges || saving) ? 'not-allowed' : 'pointer',
              opacity: (!hasChanges || saving) ? 0.6 : 1,
              animation: hasChanges ? 'pulse 2s infinite' : 'none'
            }}
          >
            {saving ? 'Publishing...' : hasChanges ? 'Publish Changes' : 'No Changes'}
          </button>
        </div>
      </div>

      {/* Header */}
      <div className="admin-header">
        <div>
          <h1 className="admin-title">Admin Setlist Controller</h1>
          <p className="admin-subtitle">
            Adjust song rankings with sliders. Changes will update the live setlist for all users.
          </p>
        </div>
      </div>
      
      {/* Status Messages */}
      {error && (
        <div className="alert alert-error">
          {error}
        </div>
      )}
      
      {success && (
        <div className="alert alert-success">
          Changes published successfully! Live setlist updated.
        </div>
      )}
      
      {/* Simulation Controls */}
      <SimulationControls />
      
      {/* Preview Panel */}
      <div className="preview-panel">
        <h3>Live Preview - Top 10</h3>
        <div className="preview-list">
          {sortedSongs.slice(0, 10).map((song, index) => (
            <motion.div
              key={song.title}
              layout
              className={`preview-item rank-${index + 1}`}
            >
              <span className="preview-rank">
                {String(index + 1).padStart(2, '0')}
              </span>
              <span className="preview-title">{song.title}</span>
              <span className="preview-score">{song.score.toFixed(3)}</span>
            </motion.div>
          ))}
        </div>
      </div>
      
      {/* Controls Panel */}
      <div className="controls-panel">
        <h3>Song Rankings ({songs.length} songs)</h3>
        <div className="controls-list">
          {sortedSongs.map((song, index) => (
            <div key={song.title} className="control-item">
              <div className="control-header">
                <span className="control-rank">#{song.rank}</span>
                <span className="control-title">{song.title}</span>
                <span className="control-score-value">{song.score.toFixed(3)}</span>
              </div>
              <div className="control-slider">
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.001"
                  value={song.score}
                  onChange={(e) => updateSongScore(song.title, parseFloat(e.target.value))}
                  className="slider"
                />
                <div className="slider-labels">
                  <span>0.000</span>
                  <span>0.500</span>
                  <span>1.000</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Footer */}
      <div className="admin-footer">
        <p className="footer-note">
          Tip: Higher scores = higher rankings. Changes are saved to Firebase when you click "Publish Changes".
        </p>
        <p className="footer-warning">
          Warning: Publishing will immediately update the live setlist visible to all users.
        </p>
      </div>
    </div>
  );
};

export default AdminSetlistController;
